import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useAuth } from '../../hooks/useAuth';

/**
 * TwoFactorChallenge — the modal presented after password authentication
 * when a user's Cognito ID token carries `custom:twofa_method=email_magic_link`.
 *
 * Flow
 * ----
 * 1. AuthProvider flips `twofa.status` to 'pending' inside signIn after it
 *    calls POST /2fa/initiate. The modal mounts on that transition.
 * 2. On mount, start a 3-second polling loop against GET /2fa/status/{id}.
 *    Optional exponential backoff kicks in past 60s (→ 5s) and 120s (→ 10s)
 *    to soften long-tail server load without complicating the state machine.
 * 3. When the poll returns 'verified', AuthProvider finalizes the session.
 *    When it returns 'expired' or 'failed', show an error + resend option.
 * 4. "Cancel" tears down the pending auth state (amplifySignOut + clear
 *    sessionStorage) and returns the user to the Login screen.
 *
 * Rendering
 * ---------
 * Mounted unconditionally by AuthProvider; self-gates on twofa.status so
 * that a "cold" mount (status='idle') renders nothing. This keeps the
 * default-off posture — with TWOFA_ENABLED=false, signIn never flips
 * status to 'pending', so the modal never opens.
 */

const POLL_BASE_MS = 3000;
const POLL_BACKOFF_AT_MS = 60_000;
const POLL_BACKOFF_5S_MS = 5000;
const POLL_BACKOFF_AT_2_MS = 120_000;
const POLL_BACKOFF_10S_MS = 10_000;

function computePollInterval(elapsedMs: number): number {
  if (elapsedMs >= POLL_BACKOFF_AT_2_MS) return POLL_BACKOFF_10S_MS;
  if (elapsedMs >= POLL_BACKOFF_AT_MS) return POLL_BACKOFF_5S_MS;
  return POLL_BASE_MS;
}

export default function TwoFactorChallenge() {
  const { twofa, pollTwoFaStatus, cancelTwoFa, user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [canceling, setCanceling] = useState(false);
  const startedAtRef = useRef<number>(Date.now());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const schedulePoll = useCallback(() => {
    const elapsed = Date.now() - startedAtRef.current;
    const delay = computePollInterval(elapsed);
    timerRef.current = setTimeout(async () => {
      try {
        const status = await pollTwoFaStatus();
        if (status === 'failed' || status === 'expired') {
          setError(
            status === 'expired'
              ? 'This verification request has expired. Please sign in again.'
              : 'Verification failed. Please try signing in again.',
          );
          return;
        }
        if (status === 'pending') {
          schedulePoll();
        }
        // 'verified' — AuthProvider will flip user; modal unmounts on re-render.
      } catch {
        schedulePoll();
      }
    }, delay);
  }, [pollTwoFaStatus]);

  useEffect(() => {
    if (twofa.status !== 'pending') return;
    startedAtRef.current = Date.now();
    schedulePoll();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [twofa.status, schedulePoll]);

  useEffect(() => {
    if (user) {
      if (timerRef.current) clearTimeout(timerRef.current);
    }
  }, [user]);

  const handleCancel = async () => {
    setCanceling(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    try {
      await cancelTwoFa();
    } finally {
      setCanceling(false);
    }
  };

  const open = twofa.status === 'pending' || !!error;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      aria-labelledby="twofa-challenge-title"
      PaperProps={{ sx: { borderRadius: 3, maxWidth: 480 } }}
    >
      <DialogTitle id="twofa-challenge-title" sx={{ pt: 4, pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <MailOutlineIcon sx={{ color: '#2D7D46' }} fontSize="large" />
          <Typography variant="h6" component="span" fontWeight={600}>
            Check your email
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {error ? (
          <Typography variant="body2" role="alert" sx={{ color: '#C0392B', mb: 1 }}>
            {error}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: '#4B5563', mb: 2 }}>
              We sent a sign-in link to your email address. Click the link to
              finish signing in. This page will update automatically when you
              do.
            </Typography>
            <Box display="flex" alignItems="center" gap={1.5} mt={1}>
              <CircularProgress size={18} sx={{ color: '#2D7D46' }} />
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Waiting for verification…
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleCancel} disabled={canceling} sx={{ color: '#6B7280' }}>
          {canceling ? 'Canceling…' : 'Cancel and sign out'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
